import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, Activity, CreditCard, ShieldCheck,
  Headphones, Bell, Megaphone, FileText, Settings, LogOut, Search, Download,
  Eye, Mail, Shield, MoreVertical, RefreshCw, CheckCircle2, AlertTriangle,
  Flame, DollarSign, X, Zap, UserCheck, CheckCircle, Trash2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import SentryHealthCheck from "@/components/admin/SentryHealthCheck";
import SentryVerificationGuide from "@/components/admin/SentryVerificationGuide";

// =====================================================
// CONSTANTS & UTILITIES
// =====================================================

const NAVY_DARK = "#0F1E35";
const NAVY = "#1F3864";
const GOLD = "#D4A843";
const AMBER = "#F4B942";
const BG = "#F0F2F5";

type AnyRec = Record<string, any>;

function getBadgeClasses(type: string, value: string): string {
  const v = (value || "").toLowerCase();
  if (type === "segment") {
    if (v === "homecare" || v === "home care") return "bg-blue-100 text-blue-800";
    if (v === "grouphome" || v === "group home") return "bg-[#1F3864] text-white";
  }
  if (type === "plan") {
    if (v === "starter") return "bg-gray-200 text-gray-800";
    if (v === "growth") return "bg-[#D4A843] text-white";
    if (v === "agency_pro" || v === "agency pro") return "bg-[#1F3864] text-white";
  }
  if (type === "status") {
    if (v === "active") return "bg-green-100 text-green-800";
    if (v === "trial") return "bg-amber-100 text-amber-800";
    if (v === "past_due" || v === "past due") return "bg-red-100 text-red-800";
    if (v === "cancelled") return "bg-gray-200 text-gray-700";
  }
  if (type === "baa") {
    if (v === "signed") return "bg-green-100 text-green-800";
    if (v === "acknowledged" || v === "pending") return "bg-amber-100 text-amber-800";
    if (v === "not_started" || v === "not started") return "bg-red-100 text-red-800";
  }
  if (type === "severity") {
    if (v === "critical") return "bg-red-100 text-red-800 border-red-300";
    if (v === "warning") return "bg-amber-100 text-amber-800 border-amber-300";
    if (v === "info") return "bg-blue-100 text-blue-800 border-blue-300";
  }
  if (type === "priority") {
    if (v === "critical") return "bg-red-100 text-red-800";
    if (v === "high") return "bg-orange-100 text-orange-800";
    if (v === "medium") return "bg-yellow-100 text-yellow-800";
    if (v === "low") return "bg-gray-200 text-gray-700";
  }
  if (type === "ticket_status") {
    if (v === "open") return "bg-red-100 text-red-800";
    if (v === "in_progress" || v === "in progress") return "bg-blue-100 text-blue-800";
    if (v === "resolved") return "bg-green-100 text-green-800";
    if (v === "closed") return "bg-gray-200 text-gray-700";
  }
  if (type === "audit") {
    const m: Record<string, string> = {
      "Org Viewed": "bg-gray-200 text-gray-700",
      "Plan Changed": "bg-blue-100 text-blue-800",
      "Account Suspended": "bg-red-100 text-red-800",
      "Account Reactivated": "bg-green-100 text-green-800",
      "BAA Marked Signed": "bg-teal-100 text-teal-800",
      "Impersonation Started": "bg-amber-100 text-amber-800",
      "Impersonation Ended": "bg-amber-100 text-amber-800",
      "Support Reply Sent": "bg-blue-100 text-blue-800",
      "Broadcast Sent": "bg-purple-100 text-purple-800",
      "Lead Deleted": "bg-red-100 text-red-800",
      "Password Reset Triggered": "bg-orange-100 text-orange-800",
      "Alert Resolved": "bg-green-100 text-green-800",
      "Portal Settings Changed": "bg-[#1F3864] text-white",
    };
    return m[value] || "bg-gray-200 text-gray-700";
  }
  return "bg-gray-200 text-gray-700";
}

function formatRelative(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtMoney(n: number): string {
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function downloadBlob(filename: string, content: string, mime = "text/csv") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

const PLAN_PRICE: Record<string, number> = { starter: 99, growth: 299, agency_pro: 599 };

// =====================================================
// PLACEHOLDER CHART DATA
// (Visual-only series for trend charts until we wire the
// real platform_metrics aggregation. Clearly sample data —
// not displayed alongside real org/alert/ticket records.)
// =====================================================

const SAMPLE_LEAD_VOLUME = Array.from({ length: 30 }, (_, i) => ({
  date: new Date(Date.now() - (29 - i) * 86400000),
  homecare: Math.round(8 + Math.sin(i / 3) * 4 + Math.random() * 6),
  grouphome: Math.round(4 + Math.cos(i / 4) * 2 + Math.random() * 4),
}));

const SAMPLE_MRR_TREND = Array.from({ length: 12 }, (_, i) => ({
  month: new Date(2025, i, 1).toLocaleDateString("en-US", { month: "short" }),
  mrr: 8000 + i * 1200 + Math.round(Math.random() * 1500),
}));

// =====================================================
// SHARED SUB-COMPONENTS
// =====================================================

function Badge({ type, value, label }: { type: string; value: string; label?: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${getBadgeClasses(type, value)}`}>
      {label ?? value}
    </span>
  );
}

function SortableTable({
  columns, data, sortField, sortDir, onSort, renderRow, emptyMessage,
}: {
  columns: { key: string; label: string; sortable?: boolean }[];
  data: AnyRec[];
  sortField: string;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
  renderRow: (row: AnyRec, i: number) => React.ReactNode;
  emptyMessage?: string;
}) {
  const sorted = useMemo(() => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const av = a[sortField]; const bv = b[sortField];
      if (av == null) return 1; if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDir]);

  return (
    <div className="overflow-x-auto bg-white rounded-xl shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map(c => (
              <th key={c.key} onClick={() => c.sortable !== false && onSort(c.key)}
                className={`text-left px-4 py-3 text-[11px] font-bold text-gray-600 uppercase tracking-wider ${c.sortable !== false ? "cursor-pointer select-none hover:bg-gray-100" : ""}`}>
                <span className="inline-flex items-center gap-1">
                  {c.label}
                  {sortField === c.key && <span>{sortDir === "asc" ? "▲" : "▼"}</span>}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sorted.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-gray-400 text-sm">{emptyMessage ?? "No records."}</td></tr>
          ) : sorted.map(renderRow)}
        </tbody>
      </table>
    </div>
  );
}

function ConfirmModal({
  open, onClose, onConfirm, title, message, destructive, confirmLabel, requirePhrase,
}: {
  open: boolean; onClose: () => void; onConfirm: () => void;
  title: string; message: string; destructive?: boolean; confirmLabel?: string; requirePhrase?: string;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  if (!open) return null;
  const canConfirm = !requirePhrase || typed === requirePhrase;
  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-[#1F3864] mb-2">{title}</h3>
        <p className="text-sm text-gray-700 mb-4">{message}</p>
        {requirePhrase && (
          <div className="mb-4">
            <label className="text-xs text-gray-600">Type <span className="font-mono font-bold">{requirePhrase}</span> to confirm:</label>
            <input value={typed} onChange={e => setTyped(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono" />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button onClick={() => { if (canConfirm) { onConfirm(); onClose(); } }}
            disabled={!canConfirm}
            className={`px-4 py-2 text-sm rounded-md text-white font-medium disabled:opacity-50 ${destructive ? "bg-red-600 hover:bg-red-700" : "bg-[#D4A843] hover:opacity-90"}`}>
            {confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// MAIN COMPONENT
// =====================================================

export default function AdminPortal() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // viewport / mobile gate
  const [viewportOk, setViewportOk] = useState(typeof window !== "undefined" ? window.innerWidth >= 1024 : true);
  useEffect(() => {
    const onResize = () => setViewportOk(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // inject Inter font once
  useEffect(() => {
    const id = "admin-inter-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id; link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
  }, []);

  // state per spec
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedOrg, setSelectedOrg] = useState<AnyRec | null>(null);
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [baaFilter, setBaaFilter] = useState("all");
  const [dateRange, setDateRange] = useState("30days");
  const [alertPanelOpen, setAlertPanelOpen] = useState(false);
  const [, setSelectedAlert] = useState<AnyRec | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonatedOrg, setImpersonatedOrg] = useState<AnyRec | null>(null);
  const [, setNotesModalOpen] = useState(false);
  const [, setSupportModalOpen] = useState(false);
  const [, setBroadcastModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; destructive?: boolean; requirePhrase?: string; onConfirm: () => void } | null>(null);

  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [sortField, setSortField] = useState("agency_name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [orgPage, setOrgPage] = useState(1);

  // platform data state
  const [platformData, setPlatformData] = useState<{
    orgs: AnyRec[]; alerts: AnyRec[]; tickets: AnyRec[]; errors: AnyRec[];
    broadcasts: AnyRec[]; audit: AnyRec[]; baaRows: AnyRec[];
  }>({
    orgs: [], alerts: [], tickets: [],
    errors: [], broadcasts: [], audit: [], baaRows: [],
  });

  // Fetch real platform data. Empty arrays render proper empty states —
  // we no longer fall back to fake records, which previously caused admins
  // to make decisions from fixture data.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [profilesRes, leadsRes, refRes, alertsRes, ticketsRes, baaRes, auditRes, broadcastsRes, errorsRes] = await Promise.all([
          supabase.from("profiles").select("id,agency_name,owner_name,agency_email,segment,plan,subscription_status,trial_ends_at,created_at,last_active_at,license_type,license_number,bed_count,dbhds_region,booking_link,service_zips,localities,service_specialties,ideal_client_payer_sources"),
          supabase.from("leads").select("id,user_id,score,created_at,status"),
          supabase.from("referral_leads").select("id,user_id"),
          supabase.from("admin_alerts" as any).select("*").order("created_at", { ascending: false }),
          supabase.from("support_tickets" as any).select("*").order("created_at", { ascending: false }),
          supabase.from("baa_status" as any).select("*"),
          supabase.from("admin_audit_log" as any).select("*").order("created_at", { ascending: false }).limit(200),
          supabase.from("admin_broadcasts" as any).select("*").order("sent_at", { ascending: false }),
          supabase.from("platform_errors" as any).select("*").order("created_at", { ascending: false }).limit(50),
        ]);
        if (cancelled) return;

        const baaMap = new Map<string, string>();
        ((baaRes.data ?? []) as any[]).forEach((b: any) => baaMap.set(b.org_id, b.status));

        const leadsByUser = new Map<string, { count: number; hot: number }>();
        ((leadsRes.data ?? []) as any[]).forEach((l: any) => {
          const cur = leadsByUser.get(l.user_id) ?? { count: 0, hot: 0 };
          cur.count++;
          if ((l.score ?? 0) >= 85) cur.hot++;
          leadsByUser.set(l.user_id, cur);
        });

        const orgs = ((profilesRes.data ?? []) as any[]).map((p: any) => ({
          ...p,
          lead_count: leadsByUser.get(p.id)?.count ?? 0,
          hot_count: leadsByUser.get(p.id)?.hot ?? 0,
          baa_status: baaMap.get(p.id) ?? "not_started",
        }));

        setPlatformData({
          orgs,
          alerts: (alertsRes.data ?? []) as any[],
          tickets: (ticketsRes.data ?? []) as any[],
          errors: (errorsRes.data ?? []) as any[],
          broadcasts: (broadcastsRes.data ?? []) as any[],
          audit: (auditRes.data ?? []) as any[],
          baaRows: (baaRes.data ?? []) as any[],
        });
      } catch (err) {
        console.error("[AdminPortal] platform data fetch failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const logAuditEvent = async (actionType: string, target?: string, details: AnyRec = {}) => {
    if (!user) return;
    try {
      await supabase.from("admin_audit_log" as any).insert({
        admin_id: user.id, action_type: actionType, target_resource_id: target ?? null, details,
      } as any);
    } catch (e) { console.warn("audit log insert failed", e); }
  };

  const dateRangeLabel = useMemo(() => {
    const m: Record<string, string> = { "7days": "Last 7 Days", "30days": "Last 30 Days", "90days": "Last 90 Days", "year": "This Year", "all": "All Time" };
    return m[dateRange] ?? "Last 30 Days";
  }, [dateRange]);

  const unresolvedAlerts = platformData.alerts.filter(a => a.status === "unresolved");

  // confirm helper
  const confirm = (cfg: { title: string; message: string; destructive?: boolean; requirePhrase?: string; onConfirm: () => void }) => {
    setConfirmAction(cfg); setConfirmModalOpen(true);
  };

  // export helper
  const doExport = async (kind: string) => {
    setExportLoading(true); setExportMenuOpen(false);
    await new Promise(r => setTimeout(r, 1500));
    let csv = "name,owner,email,plan,status\n";
    if (kind.includes("Organization")) csv += platformData.orgs.map(o => `${o.agency_name},${o.owner_name},${o.agency_email},${o.plan},${o.subscription_status}`).join("\n");
    if (kind.includes("Audit")) csv = "timestamp,admin,action,target\n" + platformData.audit.map(a => `${a.created_at},${a.admin_name ?? "Admin"},${a.action_type},${a.target ?? a.target_resource_id ?? ""}`).join("\n");
    downloadBlob(`legacygrowth-${kind.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}.csv`, csv);
    setExportLoading(false);
    logAuditEvent("Portal Settings Changed", "Export", { kind });
  };

  // org filtering
  const filteredOrgs = useMemo(() => {
    return platformData.orgs.filter(o => {
      const q = searchQuery.toLowerCase();
      if (q && !(`${o.agency_name} ${o.owner_name ?? ""} ${o.agency_email ?? ""}`.toLowerCase().includes(q))) return false;
      if (segmentFilter !== "all" && o.segment !== segmentFilter) return false;
      if (planFilter !== "all" && o.plan !== planFilter) return false;
      if (statusFilter !== "all" && o.subscription_status !== statusFilter) return false;
      if (baaFilter !== "all" && o.baa_status !== baaFilter) return false;
      return true;
    });
  }, [platformData.orgs, searchQuery, segmentFilter, planFilter, statusFilter, baaFilter]);

  const ROWS_PER_PAGE = 15;
  const orgTotalPages = Math.max(1, Math.ceil(filteredOrgs.length / ROWS_PER_PAGE));
  const pagedOrgs = filteredOrgs.slice((orgPage - 1) * ROWS_PER_PAGE, orgPage * ROWS_PER_PAGE);

  // KPIs
  const totalOrgs = platformData.orgs.length;
  const totalLeads = platformData.orgs.reduce((s, o) => s + (o.lead_count ?? 0), 0);
  const totalHot = platformData.orgs.reduce((s, o) => s + (o.hot_count ?? 0), 0);
  const mrr = platformData.orgs.filter(o => o.subscription_status === "active").reduce((s, o) => s + (PLAN_PRICE[o.plan] ?? 0), 0);
  const baaSigned = platformData.orgs.filter(o => o.baa_status === "signed").length;
  const baaRate = totalOrgs ? Math.round((baaSigned / totalOrgs) * 100) : 0;
  const openTickets = platformData.tickets.filter(t => t.status !== "resolved" && t.status !== "closed").length;

  // ===== nav config =====
  const navItems = [
    { tab: "overview", label: "Platform Overview", Icon: LayoutDashboard },
    { tab: "organizations", label: "All Organizations", Icon: Building2 },
    { tab: "leads", label: "All Leads & Inquiries", Icon: Users },
    { tab: "health", label: "Platform Health", Icon: Activity },
    { tab: "billing", label: "Billing & Revenue", Icon: CreditCard },
    { tab: "hipaa", label: "HIPAA Compliance", Icon: ShieldCheck },
    { tab: "support", label: "Support Tickets", Icon: Headphones },
    { tab: "alerts", label: "System Alerts", Icon: Bell, badge: unresolvedAlerts.length > 0 },
    { tab: "broadcast", label: "Broadcast Messages", Icon: Megaphone },
    { tab: "audit", label: "Audit Log", Icon: FileText },
    { tab: "portalsettings", label: "Portal Settings", Icon: Settings },
  ];

  const pageTitles: Record<string, string> = {
    overview: "Platform Overview", organizations: "All Organizations", leads: "All Leads & Inquiries",
    health: "Platform Health", billing: "Billing & Revenue", hipaa: "HIPAA Compliance",
    support: "Support Tickets", alerts: "System Alerts", broadcast: "Broadcast Messages",
    audit: "Audit Log", portalsettings: "Portal Settings",
  };

  // ===== mobile gate =====
  if (!viewportOk) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F2F5] p-6" style={{ fontFamily: "Inter, sans-serif" }}>
        <div className="max-w-sm text-center bg-white rounded-xl shadow p-6">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-[#1F3864] mb-2">Desktop Required</h2>
          <p className="text-sm text-gray-600">The Admin Portal is optimized for desktop. Please use a screen width of at least 1024px.</p>
        </div>
      </div>
    );
  }

  // ===== TAB CONTENT RENDERERS =====

  const renderOverview = () => (
    <div className="space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { Icon: Building2, color: "bg-blue-100 text-blue-600", label: "Total Organizations", value: totalOrgs, trend: "+3 this period" },
          { Icon: Users, color: "bg-green-100 text-green-600", label: "Total Leads Captured", value: totalLeads, trend: "+12% vs prior" },
          { Icon: Flame, color: "bg-red-100 text-red-600", label: "Hot Leads This Period", value: totalHot, trend: "+8% vs prior" },
          { Icon: DollarSign, color: "bg-amber-100 text-amber-700", label: "Monthly Recurring Revenue", value: fmtMoney(mrr), trend: "+5% vs prior" },
          { Icon: ShieldCheck, color: "bg-teal-100 text-teal-700", label: "BAA Compliance Rate", value: `${baaRate}%`, trend: baaRate >= 80 ? "Healthy" : "Below target" },
          { Icon: AlertTriangle, color: "bg-amber-100 text-amber-700", label: "Open Support Tickets", value: openTickets, trend: openTickets > 5 ? "Above norm" : "Normal" },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-5">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${k.color}`}>
              <k.Icon className="w-5 h-5" />
            </div>
            <div className="text-xs text-gray-500 font-medium">{k.label}</div>
            <div className="text-3xl font-bold text-[#1F3864] mt-1">{k.value}</div>
            <div className="text-[11px] text-gray-400 mt-2">{k.trend}</div>
          </div>
        ))}
      </div>

      <p className="text-center text-[13px] italic text-gray-500">All metrics reflect the selected date range: {dateRangeLabel}. Updated in real time.</p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Org breakdown */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-[15px] font-bold text-[#1F3864] mb-4">Organizations by Type & Plan</h3>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-gray-500 mb-2">By Segment</div>
              {[
                { label: "Home Care", count: platformData.orgs.filter(o => o.segment === "homecare").length, color: "bg-blue-500" },
                { label: "Group Home", count: platformData.orgs.filter(o => o.segment === "grouphome").length, color: "bg-[#1F3864]" },
              ].map(b => {
                const pct = totalOrgs ? Math.round((b.count / totalOrgs) * 100) : 0;
                return (
                  <div key={b.label} className="flex items-center gap-2 mb-2">
                    <span className="w-20 text-xs text-gray-700">{b.label}</span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-r-lg overflow-hidden">
                      <div className={`h-full ${b.color} rounded-r-lg`} style={{ width: `${pct || 4}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-[#1F3864] w-16 text-right">{b.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-2">By Plan</div>
              {[
                { label: "Starter", color: "bg-gray-400" },
                { label: "Growth", color: "bg-[#D4A843]" },
                { label: "Agency Pro", color: "bg-[#1F3864]" },
              ].map(p => {
                const key = p.label.toLowerCase().replace(" ", "_");
                const count = platformData.orgs.filter(o => o.plan === key).length;
                const pct = totalOrgs ? Math.round((count / totalOrgs) * 100) : 0;
                return (
                  <div key={p.label} className="flex items-center gap-2 mb-2">
                    <span className="w-20 text-xs text-gray-700">{p.label}</span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-r-lg overflow-hidden">
                      <div className={`h-full ${p.color} rounded-r-lg`} style={{ width: `${pct || 4}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-[#1F3864] w-16 text-right">{count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Lead volume chart */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-[15px] font-bold text-[#1F3864] mb-4">Lead Capture Volume — Last 30 Days</h3>
          <LineChart data={SAMPLE_LEAD_VOLUME} />
          <div className="flex gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blue-500" /> Home Care</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#1F3864]" /> Group Home</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-[15px] font-bold text-[#1F3864] mb-4">Most Active Organizations This Period</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {[...platformData.orgs].sort((a, b) => (b.lead_count ?? 0) - (a.lead_count ?? 0)).slice(0, 5).map(o => (
                <tr key={o.id}>
                  <td className="py-2.5 font-medium text-[#1F3864]">{o.agency_name}</td>
                  <td className="py-2.5"><Badge type="segment" value={o.segment ?? "homecare"} label={o.segment === "grouphome" ? "Group Home" : "Home Care"} /></td>
                  <td className="py-2.5"><Badge type="plan" value={o.plan ?? "starter"} label={(o.plan ?? "starter").replace("_", " ")} /></td>
                  <td className="py-2.5 text-gray-700">{o.lead_count ?? 0}</td>
                  <td className="py-2.5 text-xs text-gray-500">{formatRelative(o.last_active_at)}</td>
                  <td className="py-2.5 text-right">
                    <button onClick={() => { setSelectedOrg(o); setOrgDrawerOpen(true); logAuditEvent("Org Viewed", o.agency_name); }}
                      className="text-[#D4A843] text-xs font-semibold hover:underline">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-[15px] font-bold text-[#1F3864] mb-4">Recent System Alerts</h3>
          <div className="space-y-3">
            {platformData.alerts.slice(0, 5).map(a => (
              <div key={a.id} className={`pl-3 border-l-4 ${a.severity === "critical" ? "border-red-500" : a.severity === "warning" ? "border-amber-500" : "border-blue-500"}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[13px] font-bold text-[#1F3864]">{a.title}</div>
                    <div className="text-xs text-gray-600">{a.description}</div>
                  </div>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap ml-2">{formatRelative(a.created_at)}</span>
                </div>
              </div>
            ))}
            <button onClick={() => setActiveTab("alerts")} className="text-[#D4A843] text-xs font-semibold hover:underline">View All →</button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrganizations = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setOrgPage(1); }}
            placeholder="Search by org name, owner name, or email..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-[#1F3864]" />
        </div>
        {[
          { val: segmentFilter, set: setSegmentFilter, opts: [["all", "All Segments"], ["homecare", "Home Care"], ["grouphome", "Group Home"]] },
          { val: planFilter, set: setPlanFilter, opts: [["all", "All Plans"], ["starter", "Starter"], ["growth", "Growth"], ["agency_pro", "Agency Pro"]] },
          { val: statusFilter, set: setStatusFilter, opts: [["all", "All Statuses"], ["active", "Active"], ["trial", "Trial"], ["past_due", "Past Due"], ["cancelled", "Cancelled"]] },
          { val: baaFilter, set: setBaaFilter, opts: [["all", "All BAA"], ["signed", "Signed"], ["acknowledged", "Pending"], ["not_started", "Not Started"]] },
        ].map((f, i) => (
          <select key={i} value={f.val} onChange={e => { (f.set as any)(e.target.value); setOrgPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white">
            {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        ))}
        <button onClick={() => { setSearchQuery(""); setSegmentFilter("all"); setPlanFilter("all"); setStatusFilter("all"); setBaaFilter("all"); setOrgPage(1); }}
          className="text-[#D4A843] text-sm font-semibold hover:underline ml-auto">Clear Filters</button>
      </div>

      <div className="text-[13px] text-gray-600">Showing {pagedOrgs.length} of {filteredOrgs.length} organizations</div>

      <SortableTable
        columns={[
          { key: "agency_name", label: "Org Name" }, { key: "owner_name", label: "Owner" },
          { key: "segment", label: "Segment" }, { key: "plan", label: "Plan" },
          { key: "subscription_status", label: "Status" }, { key: "lead_count", label: "Leads" },
          { key: "baa_status", label: "BAA" }, { key: "created_at", label: "Joined" },
          { key: "last_active_at", label: "Last Active" }, { key: "actions", label: "Actions", sortable: false },
        ]}
        data={pagedOrgs} sortField={sortField} sortDir={sortDir}
        onSort={(k) => { if (k === sortField) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortField(k); setSortDir("asc"); } }}
        renderRow={(o) => {
          const inactive = o.last_active_at && (Date.now() - new Date(o.last_active_at).getTime()) > 30 * 86400000;
          const trialDays = o.trial_ends_at ? Math.max(0, Math.ceil((new Date(o.trial_ends_at).getTime() - Date.now()) / 86400000)) : null;
          return (
            <tr key={o.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="font-bold text-[#1F3864] text-sm">{o.agency_name}</div>
                <div className="text-xs text-gray-500">{o.agency_email}</div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">{o.owner_name ?? "—"}</td>
              <td className="px-4 py-3"><Badge type="segment" value={o.segment ?? "homecare"} label={o.segment === "grouphome" ? "Group Home" : "Home Care"} /></td>
              <td className="px-4 py-3 space-x-1">
                <Badge type="plan" value={o.plan ?? "starter"} label={(o.plan ?? "starter").replace("_", " ")} />
                {o.subscription_status === "trial" && trialDays != null && <Badge type="status" value="trial" label={`Trial — ${trialDays}d left`} />}
              </td>
              <td className="px-4 py-3"><Badge type="status" value={o.subscription_status ?? "active"} /></td>
              <td className="px-4 py-3 text-sm text-gray-800">
                <div className="font-semibold">{o.lead_count ?? 0}</div>
                <div className="text-[11px] text-gray-500">{o.hot_count ?? 0} hot this month</div>
              </td>
              <td className="px-4 py-3">
                <button onClick={() => o.baa_status !== "signed" && confirm({ title: "Send BAA Action", message: `Take action on BAA for ${o.agency_name}?`, onConfirm: () => logAuditEvent("BAA Marked Signed", o.agency_name) })}>
                  <Badge type="baa" value={o.baa_status ?? "not_started"} label={o.baa_status === "acknowledged" ? "Pending" : o.baa_status === "not_started" ? "Not Started" : "Signed"} />
                </button>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(o.created_at)}</td>
              <td className={`px-4 py-3 text-sm ${inactive ? "text-red-600 font-semibold" : "text-gray-600"}`}>{formatRelative(o.last_active_at)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <button title="View" onClick={() => { setSelectedOrg(o); setOrgDrawerOpen(true); logAuditEvent("Org Viewed", o.agency_name); }}
                    className="text-blue-600 hover:bg-blue-50 p-1 rounded"><Eye className="w-4 h-4" /></button>
                  <button title="Message" className="text-gray-600 hover:bg-gray-100 p-1 rounded"><Mail className="w-4 h-4" /></button>
                  <button title="HIPAA" className="text-teal-600 hover:bg-teal-50 p-1 rounded"><Shield className="w-4 h-4" /></button>
                  <button title="More" className="text-gray-600 hover:bg-gray-100 p-1 rounded"><MoreVertical className="w-4 h-4" /></button>
                </div>
              </td>
            </tr>
          );
        }}
      />

      <div className="flex justify-between items-center text-sm text-gray-600">
        <span>Total: {filteredOrgs.length}</span>
        <div className="flex items-center gap-3">
          <button disabled={orgPage === 1} onClick={() => setOrgPage(p => p - 1)} className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50">Previous</button>
          <span>Page {orgPage} of {orgTotalPages}</span>
          <button disabled={orgPage === orgTotalPages} onClick={() => setOrgPage(p => p + 1)} className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50">Next</button>
        </div>
      </div>
    </div>
  );

  const renderLeads = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Leads in Range", value: totalLeads },
          { label: "Average Score", value: 73 },
          { label: "Emergency Leads (100)", value: 4 },
        ].map((t, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-5">
            <div className="text-xs text-gray-500">{t.label}</div>
            <div className="text-3xl font-bold text-[#1F3864] mt-1">{t.value}</div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button onClick={() => doExport("Leads View")} className="px-3 py-2 text-sm border border-[#1F3864] text-[#1F3864] rounded-md hover:bg-[#1F3864] hover:text-white">
          Export This View (CSV)
        </button>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-400">
        Global leads table — populated from <code>leads</code> across all subscribers. Click an org name to open its drawer.
      </div>
    </div>
  );

  const renderHealth = () => {
    const services = [
      "Supabase Database", "OpenAI API", "Twilio SMS", "Resend Email",
      "AI Lead Scoring Engine", "AI Enrichment Service", "Chatbot Widget CDN", "Admin Portal",
    ];
    return (
      <div className="space-y-6">
        <section className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-[#1F3864]">Service Status</h3>
            <button className="text-sm border border-[#1F3864] text-[#1F3864] rounded px-3 py-1.5 inline-flex items-center gap-2 hover:bg-[#1F3864] hover:text-white">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh Status
            </button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {services.map(s => (
              <div key={s} className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-[#1F3864]">{s}</div>
                  <div className="text-xs text-green-600">Operational</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 mt-3">Last checked: {new Date().toLocaleTimeString()}</div>
        </section>

        <section className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-lg font-bold text-[#1F3864] mb-4">API Usage This Period</h3>
          {[
            { name: "OpenAI API", used: 12450, limit: 25000 },
            { name: "Twilio SMS", used: 380, limit: 1000 },
            { name: "Resend Emails", used: 2100, limit: 5000 },
          ].map(b => {
            const pct = Math.round((b.used / b.limit) * 100);
            const color = pct > 85 ? "bg-red-500" : pct > 60 ? "bg-amber-500" : "bg-green-500";
            return (
              <div key={b.name} className="flex items-center gap-3 mb-3">
                <span className="w-32 text-sm text-gray-700">{b.name}</span>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
                <span className="text-xs text-gray-600 w-32 text-right">{b.used.toLocaleString()} / {b.limit.toLocaleString()}</span>
              </div>
            );
          })}
          <p className="text-xs text-gray-500 mt-3">Usage resets on the 1st of each month. Upgrade API tier in Portal Settings if approaching limits.</p>
        </section>

        <section className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-lg font-bold text-[#1F3864] mb-4">Recent Errors & Warnings</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500"><tr>
              <th className="text-left py-2">Time</th><th className="text-left">Severity</th><th className="text-left">Service</th>
              <th className="text-left">Message</th><th className="text-left">Resolved</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {platformData.errors.map(e => (
                <tr key={e.id}>
                  <td className="py-2 text-xs text-gray-500">{formatRelative(e.created_at)}</td>
                  <td><Badge type="severity" value={e.severity} /></td>
                  <td className="text-sm">{e.service}</td>
                  <td className="text-sm text-gray-700">{e.message}</td>
                  <td>{e.resolved ? <Badge type="status" value="active" label="Resolved" /> : <button className="text-[#D4A843] text-xs font-semibold hover:underline">Mark Resolved</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-lg font-bold text-[#1F3864] mb-4">AI Enrichment Queue</h3>
          <p className="text-sm text-gray-600 mb-3">0 pending / 0 processing / 0 failed</p>
          <div className="text-center text-gray-400 py-6 text-sm">Queue is empty.</div>
        </section>
      </div>
    );
  };

  const renderBilling = () => {
    const arr = mrr * 12;
    const arpu = totalOrgs ? Math.round(mrr / totalOrgs) : 0;
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "MRR", value: fmtMoney(mrr) },
            { label: "ARR", value: fmtMoney(arr) },
            { label: "ARPU", value: fmtMoney(arpu) },
            { label: "Churn Rate", value: "2.1%" },
          ].map((k, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-5">
              <div className="text-xs text-gray-500">{k.label}</div>
              <div className="text-3xl font-bold text-[#1F3864] mt-1">{k.value}</div>
            </div>
          ))}
        </div>

        <section className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-lg font-bold text-[#1F3864] mb-4">MRR — Last 12 Months</h3>
          <AreaChart data={SAMPLE_MRR_TREND} />
        </section>

        <div className="grid grid-cols-2 gap-4">
          <section className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-base font-bold text-[#1F3864] mb-3">Plan Distribution</h3>
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500"><tr><th className="text-left py-1">Plan</th><th className="text-right">Count</th><th className="text-right">MRR</th><th className="text-right">% MRR</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {["starter", "growth", "agency_pro"].map(p => {
                  const orgs = platformData.orgs.filter(o => o.plan === p && o.subscription_status === "active");
                  const m = orgs.length * (PLAN_PRICE[p] ?? 0);
                  const pct = mrr ? Math.round((m / mrr) * 100) : 0;
                  return (
                    <tr key={p}>
                      <td className="py-2"><Badge type="plan" value={p} label={p.replace("_", " ")} /></td>
                      <td className="text-right">{orgs.length}</td>
                      <td className="text-right">{fmtMoney(m)}</td>
                      <td className="text-right">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
          <section className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-base font-bold text-[#1F3864] mb-3">Trial Status</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center"><div className="text-2xl font-bold text-[#1F3864]">{platformData.orgs.filter(o => o.subscription_status === "trial").length}</div><div className="text-[11px] text-gray-500">Active Trials</div></div>
              <div className="text-center"><div className="text-2xl font-bold text-amber-600">2</div><div className="text-[11px] text-gray-500">Ending in 7d</div></div>
              <div className="text-center"><div className="text-2xl font-bold text-green-600">68%</div><div className="text-[11px] text-gray-500">Trial→Paid</div></div>
            </div>
            <div className="space-y-2">
              {platformData.orgs.filter(o => o.subscription_status === "trial").slice(0, 5).map(o => {
                const days = o.trial_ends_at ? Math.max(0, Math.ceil((new Date(o.trial_ends_at).getTime() - Date.now()) / 86400000)) : 0;
                return (
                  <div key={o.id} className="flex justify-between items-center text-sm border border-gray-100 rounded p-2">
                    <div><div className="font-semibold text-[#1F3864]">{o.agency_name}</div><div className="text-xs text-gray-500">{o.agency_email} • {days}d left</div></div>
                    <button className="text-[#D4A843] text-xs font-semibold hover:underline">Send Nudge</button>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <section className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-red-500">
          <h3 className="text-lg font-bold text-[#1F3864] mb-3">Accounts Requiring Attention</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500"><tr><th className="text-left py-2">Org</th><th className="text-left">Plan</th><th className="text-left">Days Past Due</th><th className="text-left">Last Login</th><th></th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {platformData.orgs.filter(o => o.subscription_status === "past_due" || (o.last_active_at && Date.now() - new Date(o.last_active_at).getTime() > 21 * 86400000)).map(o => (
                <tr key={o.id}>
                  <td className="py-2 font-medium text-[#1F3864]">{o.agency_name}</td>
                  <td><Badge type="plan" value={o.plan} label={o.plan.replace("_", " ")} /></td>
                  <td className="text-red-600 font-semibold">{o.subscription_status === "past_due" ? "5 days" : "—"}</td>
                  <td className="text-gray-600">{formatRelative(o.last_active_at)}</td>
                  <td className="text-right space-x-2">
                    <button className="text-[#D4A843] text-xs font-semibold hover:underline">Send Reminder</button>
                    <button onClick={() => confirm({ title: "Suspend Account", message: `Suspend ${o.agency_name}?`, destructive: true, onConfirm: () => logAuditEvent("Account Suspended", o.agency_name) })}
                      className="text-red-600 text-xs font-semibold hover:underline">Suspend</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    );
  };

  const renderHipaa = () => {
    const pending = platformData.orgs.filter(o => o.baa_status === "acknowledged").length;
    const notStarted = platformData.orgs.filter(o => o.baa_status === "not_started").length;
    const barColor = baaRate > 80 ? "bg-green-500" : baaRate > 60 ? "bg-amber-500" : "bg-red-500";
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-5 border-t-4 border-green-500">
            <div className="text-xs text-gray-500">BAA Signed</div>
            <div className="text-3xl font-bold text-green-600 mt-1">{baaSigned}</div>
            <div className="text-xs text-gray-500">{baaRate}%</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border-t-4 border-amber-500">
            <div className="text-xs text-gray-500">BAA Pending</div>
            <div className="text-3xl font-bold text-amber-600 mt-1">{pending}</div>
            <div className="text-xs text-gray-500">{totalOrgs ? Math.round((pending / totalOrgs) * 100) : 0}%</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border-t-4 border-red-500">
            <div className="text-xs text-gray-500">BAA Not Started</div>
            <div className="text-3xl font-bold text-red-600 mt-1">{notStarted}</div>
            <div className="text-xs text-gray-500">{totalOrgs ? Math.round((notStarted / totalOrgs) * 100) : 0}%</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="text-sm font-semibold text-[#1F3864] mb-2 text-center">Platform BAA Compliance Rate: {baaRate}%</div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full ${barColor}`} style={{ width: `${baaRate}%` }} /></div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-lg font-bold text-[#1F3864] mb-3">BAA Status</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500"><tr><th className="text-left py-2">Org</th><th className="text-left">Owner</th><th className="text-left">Segment</th><th className="text-left">BAA</th><th className="text-left">Document</th><th></th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {platformData.orgs.map(o => (
                <tr key={o.id}>
                  <td className="py-2 font-medium text-[#1F3864]">{o.agency_name}</td>
                  <td className="text-gray-700">{o.owner_name}</td>
                  <td><Badge type="segment" value={o.segment} label={o.segment === "grouphome" ? "Group Home" : "Home Care"} /></td>
                  <td><Badge type="baa" value={o.baa_status} label={o.baa_status === "acknowledged" ? "Pending" : o.baa_status === "not_started" ? "Not Started" : "Signed"} /></td>
                  <td className="text-xs text-gray-500">{o.baa_status === "signed" ? <a className="text-[#D4A843] hover:underline" href="#">View</a> : "Not uploaded"}</td>
                  <td className="text-right space-x-2 text-xs">
                    {o.baa_status !== "signed" && <button className="text-[#D4A843] font-semibold hover:underline">Send Reminder</button>}
                    {o.baa_status === "acknowledged" && <button onClick={() => confirm({ title: "Mark as Signed", message: `Mark ${o.agency_name}'s BAA as signed?`, onConfirm: () => logAuditEvent("BAA Marked Signed", o.agency_name) })} className="text-green-700 font-semibold hover:underline">Mark Signed</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSupport = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3">
        <input placeholder="Search by org name or subject..." className="flex-1 min-w-[260px] px-3 py-2 border border-gray-200 rounded-md text-sm" />
        <select className="px-3 py-2 border border-gray-200 rounded-md text-sm"><option>All Statuses</option><option>Open</option><option>In Progress</option><option>Resolved</option></select>
        <select className="px-3 py-2 border border-gray-200 rounded-md text-sm"><option>All Priorities</option><option>Critical</option><option>High</option><option>Medium</option><option>Low</option></select>
      </div>
      <SortableTable
        columns={[
          { key: "id", label: "Ticket" }, { key: "org_name", label: "Org" }, { key: "subject", label: "Subject" },
          { key: "priority", label: "Priority" }, { key: "status", label: "Status" }, { key: "created_at", label: "Created" },
          { key: "assigned_to_name", label: "Assigned" }, { key: "actions", label: "Actions", sortable: false },
        ]}
        data={platformData.tickets} sortField="created_at" sortDir="desc" onSort={() => {}}
        renderRow={(t) => (
          <tr key={t.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 text-xs font-mono text-gray-500">#{String(t.id).slice(0, 6)}</td>
            <td className="px-4 py-3 text-sm font-medium text-[#1F3864]">{t.org_name ?? "—"}</td>
            <td className="px-4 py-3 text-sm">{t.subject}</td>
            <td className="px-4 py-3"><Badge type="priority" value={t.priority} /></td>
            <td className="px-4 py-3"><Badge type="ticket_status" value={t.status} label={t.status.replace("_", " ")} /></td>
            <td className="px-4 py-3 text-xs text-gray-500">{formatRelative(t.created_at)}</td>
            <td className="px-4 py-3 text-sm text-gray-600">{t.assigned_to_name ?? "Unassigned"}</td>
            <td className="px-4 py-3">
              <div className="flex gap-2">
                <button className="text-blue-600 hover:bg-blue-50 p-1 rounded"><Eye className="w-4 h-4" /></button>
                <button className="text-gray-600 hover:bg-gray-100 p-1 rounded"><UserCheck className="w-4 h-4" /></button>
                <button className="text-green-600 hover:bg-green-50 p-1 rounded"><CheckCircle className="w-4 h-4" /></button>
              </div>
            </td>
          </tr>
        )}
      />
    </div>
  );

  const renderAlerts = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-lg font-bold text-[#1F3864] mb-3">Alert Rules</h3>
        <div className="divide-y divide-gray-100">
          {[
            { name: "BAA Compliance Drop", condition: "Compliance < 70%", severity: "critical", active: true },
            { name: "Trial Expiring Soon", condition: "7 days before trial end", severity: "warning", active: true },
            { name: "API Error Rate High", condition: "Error rate > 5% in 1h", severity: "critical", active: true },
            { name: "Inactive Subscriber", condition: "No login in 21 days", severity: "warning", active: true },
            { name: "Emergency Lead Surge", condition: "3+ emergency leads in 1h", severity: "info", active: true },
            { name: "Past Due Account", condition: "Payment failed", severity: "critical", active: true },
          ].map(r => (
            <div key={r.name} className="flex items-center py-2">
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#1F3864]">{r.name}</div>
                <div className="text-xs text-gray-500">{r.condition}</div>
              </div>
              <Badge type="severity" value={r.severity} />
              <div className="ml-3 w-10 h-5 rounded-full bg-green-500 relative cursor-pointer"><div className="absolute right-0.5 top-0.5 w-4 h-4 rounded-full bg-white" /></div>
              <button className="ml-3 text-[#D4A843] text-xs font-semibold hover:underline">Edit</button>
            </div>
          ))}
        </div>
      </div>

      <SortableTable
        columns={[
          { key: "created_at", label: "Time" }, { key: "severity", label: "Severity" },
          { key: "alert_type", label: "Type" }, { key: "title", label: "Title" },
          { key: "status", label: "Status" }, { key: "actions", label: "Actions", sortable: false },
        ]}
        data={platformData.alerts} sortField="created_at" sortDir="desc" onSort={() => {}}
        renderRow={(a) => (
          <tr key={a.id} className={`hover:bg-gray-50 ${a.severity === "critical" && a.status === "unresolved" ? "bg-red-50/50" : ""}`}>
            <td className="px-4 py-3 text-xs text-gray-500">{formatRelative(a.created_at)}</td>
            <td className="px-4 py-3"><Badge type="severity" value={a.severity} /></td>
            <td className="px-4 py-3 text-sm">{a.alert_type}</td>
            <td className="px-4 py-3 text-sm font-medium text-[#1F3864]">{a.title}</td>
            <td className="px-4 py-3 text-xs">{a.status === "unresolved" ? <span className="text-red-600 font-semibold">Unresolved</span> : <span className="text-green-600 font-semibold">Resolved</span>}</td>
            <td className="px-4 py-3 text-xs space-x-2">
              {a.status === "unresolved" && <>
                <button onClick={() => confirm({ title: "Resolve Alert", message: `Mark "${a.title}" resolved?`, onConfirm: () => logAuditEvent("Alert Resolved", a.title) })} className="text-[#D4A843] font-semibold hover:underline">Resolve</button>
                <button className="text-[#1F3864] font-semibold hover:underline">Investigate</button>
              </>}
            </td>
          </tr>
        )}
      />
    </div>
  );

  const renderBroadcast = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-7">
        <h3 className="text-lg font-bold text-[#1F3864] mb-4">Compose Broadcast Message</h3>
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-600 mb-2 block">Audience</label>
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            {["All Subscribers", "Home Care Only", "Group Home Only", "Custom Selection"].map(a => (
              <button key={a} className="px-4 py-2 text-sm hover:bg-gray-50 border-r border-gray-200 last:border-0">{a}</button>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-600 mb-1 block">Subject</label>
          <input className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm" placeholder="Subject..." />
        </div>
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-600 mb-1 block">Message</label>
          <div contentEditable suppressContentEditableWarning className="min-h-[200px] border border-gray-200 rounded-md p-3 text-sm focus:outline-none focus:border-[#1F3864]" />
          <div className="text-xs text-gray-500 mt-2">Tokens: {`{org_name}`} {`{owner_name}`} {`{plan}`} {`{days_remaining}`}</div>
        </div>
        <div className="flex justify-end gap-2">
          <button className="px-4 py-2 text-sm border border-[#1F3864] text-[#1F3864] rounded-md">Preview Message</button>
          <button onClick={() => confirm({ title: "Send Broadcast", message: `You are about to send a message to ${totalOrgs} organizations. Continue?`, onConfirm: () => logAuditEvent("Broadcast Sent", "All Subscribers") })}
            className="px-6 py-2 text-sm bg-[#D4A843] text-white rounded-md font-semibold hover:opacity-90">Send Broadcast</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-base font-bold text-[#1F3864] mb-3">Broadcast History</h3>
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-500"><tr><th className="text-left py-2">Sent</th><th className="text-left">Subject</th><th className="text-left">Audience</th><th className="text-right">Delivered</th><th className="text-right">Open Rate</th><th className="text-left">By</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {platformData.broadcasts.map(b => (
              <tr key={b.id}>
                <td className="py-2 text-xs text-gray-500">{formatDate(b.sent_at)}</td>
                <td className="text-sm">{b.subject}</td>
                <td className="text-xs text-gray-600">{b.audience}</td>
                <td className="text-right text-sm">{b.sent_count}</td>
                <td className="text-right text-sm">{b.open_rate ? Math.round(b.open_rate * 100) + "%" : "—"}</td>
                <td className="text-xs text-gray-600">{b.sender_name ?? "Admin"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAudit = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3">
        <input placeholder="Search by org name or action type..." className="flex-1 min-w-[260px] px-3 py-2 border border-gray-200 rounded-md text-sm" />
        <select className="px-3 py-2 border border-gray-200 rounded-md text-sm"><option>All Admins</option></select>
        <button onClick={() => doExport("Audit Log")} className="px-3 py-2 text-sm border border-[#1F3864] text-[#1F3864] rounded-md ml-auto">Export Full Audit Log (CSV)</button>
      </div>
      <SortableTable
        columns={[
          { key: "created_at", label: "Time" }, { key: "admin_name", label: "Admin" },
          { key: "action_type", label: "Action" }, { key: "target", label: "Target" },
          { key: "details", label: "Details" }, { key: "ip_address", label: "IP" },
        ]}
        data={platformData.audit} sortField="created_at" sortDir="desc" onSort={() => {}}
        renderRow={(a) => (
          <tr key={a.id}>
            <td className="px-4 py-3 text-xs text-gray-500">{formatRelative(a.created_at)}</td>
            <td className="px-4 py-3 text-sm">{a.admin_name ?? "Admin"}</td>
            <td className="px-4 py-3"><Badge type="audit" value={a.action_type} /></td>
            <td className="px-4 py-3 text-sm">{a.target ?? a.target_resource_id ?? "—"}</td>
            <td className="px-4 py-3 text-xs text-gray-600">{typeof a.details === "string" ? a.details : JSON.stringify(a.details ?? {})}</td>
            <td className="px-4 py-3 text-xs font-mono text-gray-500">{a.ip_address ?? "—"}</td>
          </tr>
        )}
      />
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-4">
      <SentryVerificationGuide />
      <SentryHealthCheck />
      {[
        { title: "Admin Users", body: "Manage who has admin access to the portal." },
        { title: "Alert Thresholds", body: "BAA floor: 70% • Inactive: 21 days • API errors: 5% • Trial reminder: 7 days" },
        { title: "API Keys & Integrations", body: "OpenAI, Twilio, Resend, Supabase service key — masked for safety." },
        { title: "BAA Template Management", body: "Upload and version the platform-wide BAA template." },
        { title: "Email Templates — Platform Defaults", body: "Edit the 14 default outreach templates used across all subscribers." },
      ].map(c => (
        <div key={c.title} className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-base font-bold text-[#1F3864] mb-1">{c.title}</h3>
          <p className="text-sm text-gray-600">{c.body}</p>
        </div>
      ))}
      <div className="bg-white rounded-xl shadow-sm p-5 border-2 border-red-300">
        <h3 className="text-base font-bold text-red-700 mb-3">Danger Zone</h3>
        <div className="space-y-2">
          <button onClick={() => confirm({ title: "Purge All Test Data", message: "This deletes every lead/inquiry marked as test across the platform.", destructive: true, requirePhrase: "PURGE TEST DATA", onConfirm: () => logAuditEvent("Portal Settings Changed", "Purge Test Data") })}
            className="w-full text-left px-4 py-3 border border-red-200 rounded-md text-sm hover:bg-red-50">Purge All Test Data</button>
          <button onClick={() => confirm({ title: "Force Re-Score All Leads", message: "Re-runs the scoring algorithm on every lead in the platform.", requirePhrase: "RESCORE ALL", onConfirm: () => logAuditEvent("Portal Settings Changed", "Rescore All Leads") })}
            className="w-full text-left px-4 py-3 border border-red-200 rounded-md text-sm hover:bg-red-50">Force Re-Score All Leads</button>
          <button onClick={() => confirm({ title: "Take Platform Offline", message: "All subscribers will see a maintenance page until you bring it back online.", destructive: true, requirePhrase: "MAINTENANCE MODE", onConfirm: () => logAuditEvent("Portal Settings Changed", "Maintenance Mode On") })}
            className="w-full text-left px-4 py-3 border border-red-300 rounded-md text-sm bg-red-50 hover:bg-red-100 text-red-700 font-semibold">Take Platform Offline</button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview": return renderOverview();
      case "organizations": return renderOrganizations();
      case "leads": return renderLeads();
      case "health": return renderHealth();
      case "billing": return renderBilling();
      case "hipaa": return renderHipaa();
      case "support": return renderSupport();
      case "alerts": return renderAlerts();
      case "broadcast": return renderBroadcast();
      case "audit": return renderAudit();
      case "portalsettings": return renderSettings();
      default: return null;
    }
  };

  // ===== RENDER =====
  return (
    <div style={{ fontFamily: "Inter, sans-serif" }} className={`min-h-screen ${impersonating ? "border-4 border-amber-400" : ""}`}>
      {impersonating && impersonatedOrg && (
        <div className="w-full bg-amber-400 text-[#1F3864] text-[13px] font-semibold py-2 px-4 text-center">
          You are viewing as {impersonatedOrg.agency_name} — {impersonatedOrg.owner_name}. All changes affect their real account.{" "}
          <button onClick={() => { setImpersonating(false); setImpersonatedOrg(null); logAuditEvent("Impersonation Ended", impersonatedOrg.agency_name); }}
            className="text-red-700 font-bold ml-2 underline">EXIT IMPERSONATION</button>
        </div>
      )}
      <div className="w-full bg-amber-400 text-[#0F1E35] font-bold text-[13px] py-1.5 px-4 text-center">
        INTERNAL USE ONLY — LegacyGrowth
      </div>

      <div className="flex" style={{ background: BG, minHeight: "calc(100vh - 28px)" }}>
        {/* Sidebar */}
        <aside style={{ background: NAVY_DARK, width: 260 }} className={`shrink-0 flex flex-col text-white sticky top-0 h-screen ${impersonating ? "border-l-4 border-amber-400" : ""}`}>
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5" style={{ color: GOLD }} />
              <span className="text-[17px] font-bold">LegacyGrowth Engine</span>
            </div>
            <span className="inline-block mt-2 px-2 py-0.5 bg-red-700 text-white rounded-full text-[11px] font-bold tracking-wider">ADMIN PORTAL</span>
            <div className="mt-3 text-[13px] text-gray-300">{user?.email ?? "Admin"}</div>
            <div className="text-[12px]" style={{ color: GOLD }}>LegacyGrowth</div>
          </div>
          <div className="border-b mx-5" style={{ borderColor: GOLD, opacity: 0.5 }} />
          <nav className="flex-1 py-3">
            {navItems.map(item => {
              const active = activeTab === item.tab;
              return (
                <button key={item.tab} onClick={() => setActiveTab(item.tab)}
                  className={`w-full px-4 py-2.5 mx-2 rounded-lg flex items-center gap-3 text-[13px] transition ${active ? "border-l-[3px] font-bold text-white" : "text-gray-300 hover:bg-white/5"}`}
                  style={active ? { background: "rgba(212,168,67,0.15)", borderLeftColor: GOLD } : undefined}>
                  <item.Icon className="w-5 h-5 shrink-0" style={{ color: active ? GOLD : undefined }} />
                  <span className="flex-1 text-left">{item.label}{impersonating && impersonatedOrg ? ` (as ${impersonatedOrg.agency_name})` : ""}</span>
                  {item.badge && <span className="w-2 h-2 rounded-full bg-red-500" />}
                </button>
              );
            })}
          </nav>
          <button onClick={() => navigate("/")}
            className="px-5 py-3 flex items-center gap-2 text-red-400 hover:bg-white/5 text-sm font-medium border-t border-white/10">
            <LogOut className="w-4 h-4" /> Exit Admin Portal
          </button>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 h-16 sticky top-0 z-30 flex items-center px-6 gap-3">
            <h1 className="text-[20px] font-bold text-[#1F3864] flex-1">{pageTitles[activeTab]}</h1>

            <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-md bg-white">
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>

            <div className="relative">
              <button onClick={() => setExportMenuOpen(o => !o)} disabled={exportLoading}
                className="px-3 py-1.5 text-sm border border-[#1F3864] text-[#1F3864] rounded-md inline-flex items-center gap-2 hover:bg-[#1F3864] hover:text-white">
                {exportLoading ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Export Data
              </button>
              {exportMenuOpen && (
                <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-40">
                  {["Export Organizations (CSV)", "Export All Leads (CSV)", "Export Billing Summary (CSV)", "Export HIPAA Status Report (PDF)"].map(o => (
                    <button key={o} onClick={() => doExport(o)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">{o}</button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => setAlertPanelOpen(true)} className="relative p-2 hover:bg-gray-100 rounded-md">
              <Bell className="w-5 h-5 text-gray-600" />
              {unresolvedAlerts.length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{unresolvedAlerts.length}</span>
              )}
            </button>

            <div className="relative">
              <button onClick={() => setAvatarMenuOpen(o => !o)} className="w-9 h-9 rounded-full bg-[#1F3864] text-[#D4A843] font-bold text-sm flex items-center justify-center">ET</button>
              {avatarMenuOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-40">
                  <button className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">Admin Profile</button>
                  <button onClick={() => { setActiveTab("portalsettings"); setAvatarMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50">Portal Settings</button>
                  <button onClick={() => signOut()} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-red-600">Sign Out</button>
                </div>
              )}
            </div>
          </header>

          {/* Content */}
          <div className="p-6">{renderTabContent()}</div>
        </main>

        {/* Alert slide panel */}
        {alertPanelOpen && (
          <div className="fixed inset-0 z-40" onClick={() => setAlertPanelOpen(false)}>
            <div className="absolute right-0 top-0 h-full w-[360px] bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold text-[#1F3864]">System Alerts</h3>
                <div className="flex gap-2 items-center">
                  <button className="text-[#D4A843] text-xs font-semibold hover:underline">Mark All Resolved</button>
                  <button onClick={() => setAlertPanelOpen(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {unresolvedAlerts.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No active alerts. Platform is healthy.</p>
                  </div>
                ) : unresolvedAlerts.map(a => (
                  <div key={a.id} className={`p-3 rounded-md border-l-4 bg-gray-50 ${a.severity === "critical" ? "border-red-500" : a.severity === "warning" ? "border-amber-500" : "border-blue-500"}`}>
                    <Badge type="severity" value={a.severity} />
                    <div className="text-sm font-bold text-[#1F3864] mt-1.5">{a.title}</div>
                    <div className="text-xs text-gray-600">{a.description}</div>
                    <div className="text-[11px] text-gray-400 mt-1">{formatRelative(a.created_at)}</div>
                    <div className="flex gap-3 mt-2">
                      <button onClick={() => { setSelectedAlert(a); confirm({ title: "Resolve", message: `Mark "${a.title}" resolved?`, onConfirm: () => logAuditEvent("Alert Resolved", a.title) }); }} className="text-[#D4A843] text-xs font-semibold hover:underline">Resolve</button>
                      <button className="text-[#1F3864] text-xs font-semibold hover:underline">Investigate</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Org Detail Drawer */}
        {orgDrawerOpen && selectedOrg && (
          <OrgDetailDrawer
            org={selectedOrg}
            onClose={() => setOrgDrawerOpen(false)}
            onImpersonate={() => {
              confirm({
                title: "Enter Impersonation Mode",
                message: `You are about to view the platform as ${selectedOrg.agency_name}. Any changes you make will affect their real account. Continue?`,
                onConfirm: () => {
                  setImpersonating(true); setImpersonatedOrg(selectedOrg); setOrgDrawerOpen(false);
                  logAuditEvent("Impersonation Started", selectedOrg.agency_name);
                },
              });
            }}
          />
        )}

        <ConfirmModal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() => confirmAction?.onConfirm?.()}
          title={confirmAction?.title ?? ""}
          message={confirmAction?.message ?? ""}
          destructive={confirmAction?.destructive}
          requirePhrase={confirmAction?.requirePhrase}
        />
      </div>
    </div>
  );
}

// =====================================================
// SUB-COMPONENTS
// =====================================================

function OrgDetailDrawer({ org, onClose, onImpersonate }: { org: AnyRec; onClose: () => void; onImpersonate: () => void }) {
  const [tab, setTab] = useState<"profile" | "leads" | "outreach" | "billing" | "notes">("profile");
  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose}>
      <div className="absolute right-0 top-0 h-full w-[560px] bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-[22px] font-bold text-[#1F3864]">{org.agency_name}</h2>
              <div className="flex gap-2 mt-2">
                <Badge type="segment" value={org.segment ?? "homecare"} label={org.segment === "grouphome" ? "Group Home" : "Home Care"} />
                <Badge type="plan" value={org.plan ?? "starter"} label={(org.plan ?? "starter").replace("_", " ")} />
                <Badge type="status" value={org.subscription_status ?? "active"} />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={onImpersonate} className="px-3 py-1.5 text-xs bg-[#D4A843] text-white font-semibold rounded-md hover:opacity-90">View as User</button>
              <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="flex gap-1 mt-4 border-b border-gray-200 -mb-5">
            {(["profile", "leads", "outreach", "billing", "notes"] as const).map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-3 py-2 text-sm font-medium capitalize ${tab === t ? "text-[#1F3864] border-b-2 border-[#D4A843]" : "text-gray-500 hover:text-[#1F3864]"}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="p-5">
          {tab === "profile" && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["Owner", org.owner_name], ["Email", org.agency_email], ["Phone", org.owner_phone],
                ["Plan", org.plan], ["Status", org.subscription_status], ["Joined", formatDate(org.created_at)],
                ["Last Active", formatRelative(org.last_active_at)], ["Service ZIPs", (org.service_zips ?? []).join(", ") || "—"],
                ["Specialties", (org.service_specialties ?? []).join(", ") || "—"], ["Payer Types", (org.ideal_client_payer_sources ?? []).join(", ") || "—"],
                ["Booking Link", org.booking_link ?? "—"], ["DBHDS License", org.license_number ?? "—"],
                ["DBHDS Region", org.dbhds_region ?? "—"], ["Bed Count", org.bed_count ?? "—"],
              ].map(([k, v]) => (
                <div key={k as string}>
                  <div className="text-[11px] uppercase text-gray-500 font-semibold">{k}</div>
                  <div className="text-[#1F3864] font-medium">{(v as any) || "—"}</div>
                </div>
              ))}
              <div className="col-span-2"><button className="mt-2 px-4 py-2 text-sm border border-[#1F3864] text-[#1F3864] rounded-md hover:bg-[#1F3864] hover:text-white">Edit Profile</button></div>
            </div>
          )}
          {tab === "leads" && (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-50 rounded-md p-3 text-center"><div className="text-2xl font-bold text-[#1F3864]">{org.lead_count ?? 0}</div><div className="text-[11px] text-gray-500">Total</div></div>
                <div className="bg-gray-50 rounded-md p-3 text-center"><div className="text-2xl font-bold text-red-600">{org.hot_count ?? 0}</div><div className="text-[11px] text-gray-500">Hot</div></div>
                <div className="bg-gray-50 rounded-md p-3 text-center"><div className="text-2xl font-bold text-green-600">22%</div><div className="text-[11px] text-gray-500">Conversion</div></div>
              </div>
              <div className="text-center text-gray-400 py-6 text-sm">Lead list for this org.</div>
            </div>
          )}
          {tab === "outreach" && <div className="text-center text-gray-400 py-12 text-sm">Referral outreach for this org.</div>}
          {tab === "billing" && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b py-2"><span className="text-gray-500">Current Plan</span><Badge type="plan" value={org.plan} label={(org.plan ?? "starter").replace("_", " ")} /></div>
              <div className="flex justify-between border-b py-2"><span className="text-gray-500">Monthly</span><span className="font-semibold text-[#1F3864]">{fmtMoney(PLAN_PRICE[org.plan] ?? 0)}</span></div>
              <div className="flex justify-between border-b py-2"><span className="text-gray-500">Status</span><Badge type="status" value={org.subscription_status ?? "active"} /></div>
              <div className="flex justify-between border-b py-2"><span className="text-gray-500">Next Renewal</span><span>{formatDate(new Date(Date.now() + 30 * 86400000))}</span></div>
              <button className="mt-3 w-full px-4 py-2 text-sm bg-[#D4A843] text-white font-semibold rounded-md">Change Plan</button>
            </div>
          )}
          {tab === "notes" && (
            <div>
              <div className="text-center text-gray-400 py-6 text-sm">No notes yet.</div>
              <textarea placeholder="Add an internal note..." className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm" rows={3} />
              <button className="mt-2 px-4 py-2 text-sm bg-[#D4A843] text-white font-semibold rounded-md">Add Note</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LineChart({ data }: { data: { date: Date; homecare: number; grouphome: number }[] }) {
  const w = 600, h = 180, pad = 24;
  const xs = (i: number) => pad + (i / (data.length - 1)) * (w - 2 * pad);
  const max = Math.max(...data.flatMap(d => [d.homecare, d.grouphome])) || 1;
  const ys = (v: number) => h - pad - (v / max) * (h - 2 * pad);
  const path = (key: "homecare" | "grouphome") => data.map((d, i) => `${i === 0 ? "M" : "L"}${xs(i)},${ys(d[key])}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-44">
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#e5e7eb" />
      <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#e5e7eb" />
      <path d={path("homecare")} fill="none" stroke="#3b82f6" strokeWidth={2} />
      <path d={path("grouphome")} fill="none" stroke="#1F3864" strokeWidth={2} />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={xs(i)} cy={ys(d.homecare)} r={2.5} fill="#3b82f6"><title>{d.date.toLocaleDateString()}: {d.homecare} home care</title></circle>
          <circle cx={xs(i)} cy={ys(d.grouphome)} r={2.5} fill="#1F3864"><title>{d.date.toLocaleDateString()}: {d.grouphome} group home</title></circle>
          {i % 5 === 0 && <text x={xs(i)} y={h - 6} fontSize={9} textAnchor="middle" fill="#9ca3af">{d.date.toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}</text>}
        </g>
      ))}
    </svg>
  );
}

function AreaChart({ data }: { data: { month: string; mrr: number }[] }) {
  const w = 700, h = 200, pad = 30;
  const max = Math.max(...data.map(d => d.mrr)) || 1;
  const xs = (i: number) => pad + (i / (data.length - 1)) * (w - 2 * pad);
  const ys = (v: number) => h - pad - (v / max) * (h - 2 * pad);
  const line = data.map((d, i) => `${i === 0 ? "M" : "L"}${xs(i)},${ys(d.mrr)}`).join(" ");
  const area = `${line} L${xs(data.length - 1)},${h - pad} L${xs(0)},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-52">
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#e5e7eb" />
      <path d={area} fill={GOLD} fillOpacity={0.2} />
      <path d={line} fill="none" stroke={GOLD} strokeWidth={2.5} />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={xs(i)} cy={ys(d.mrr)} r={3} fill={GOLD}><title>{d.month}: {fmtMoney(d.mrr)}</title></circle>
          <text x={xs(i)} y={h - 8} fontSize={10} textAnchor="middle" fill="#6b7280">{d.month}</text>
        </g>
      ))}
    </svg>
  );
}
